import { createClient } from '@supabase/supabase-js'

// Supabase configuration - uses environment variables in production, fallback to the Mayo project URL in development
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://jwkmhgishqslmdlbhqqz.supabase.co'
const supabasePublishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY

if (!supabasePublishableKey) {
  console.error('REACT_APP_SUPABASE_PUBLISHABLE_KEY is not set')
}

// Browser client (publishable key, RLS applies). Admin operations go through the admin-users edge function.
export const supabase = createClient(supabaseUrl, supabasePublishableKey)

// Auth helper functions
export const authHelpers = {
  // Expose supabase client for direct access when needed
  supabase,

  // Session and onboarding management
  markOnboardingComplete: () => {
    localStorage.setItem('narcotictrack_onboarding_complete', 'true');
  },

  hasCompletedOnboarding: () => {
    return localStorage.getItem('narcotictrack_onboarding_complete') === 'true';
  },

  setLastActivity: () => {
    localStorage.setItem('narcotictrack_last_activity', Date.now().toString());
  },

  checkSessionTimeout: () => {
    const lastActivity = localStorage.getItem('narcotictrack_last_activity');
    if (!lastActivity) return true; // No activity recorded, consider timed out
    
    const now = Date.now();
    const timeDiff = now - parseInt(lastActivity);
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    
    return timeDiff > fiveMinutes;
  },

  clearSession: () => {
    localStorage.removeItem('narcotictrack_last_activity');
    // Don't clear onboarding - that stays forever
  },

  // Check if user has valid session and hasn't timed out
  checkValidSession: async () => {
    try {
      // Check if session timed out first
      if (authHelpers.checkSessionTimeout()) {
        await authHelpers.signOut();
        return { data: null, error: 'Session timed out' };
      }

      // Check Supabase session
      const { data, error } = await supabase.auth.getSession();
      
      if (data?.session?.user) {
        // Update activity timestamp
        authHelpers.setLastActivity();
        return { data: data.session, error: null };
      }
      
      return { data: null, error: error || 'No active session' };
    } catch (error) {
      return { data: null, error };
    }
  },
  signUp: async (email, password, userData = {}) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData, // Additional user metadata
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Sign in user
  signIn: async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (data?.session?.user) {
        // Set activity timestamp on successful login
        authHelpers.setLastActivity();
      }

      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Sign in with Google OAuth
  signInWithGoogle: async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Sign in with OTP (Magic Link)
  signInWithOTP: async (email) => {
    try {
      const { data, error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Verify OTP code
  verifyOTP: async (email, token) => {
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email'
      })

      if (data?.session?.user) {
        // Set activity timestamp on successful login
        authHelpers.setLastActivity();
      }

      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Sign out user
  signOut: async () => {
    try {
      authHelpers.clearSession();
      const { error } = await supabase.auth.signOut()
      return { error }
    } catch (error) {
      return { error }
    }
  },

  // Reset password
  resetPassword: async (email) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/password-reset`
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Update password (used after reset)
  updatePassword: async (newPassword) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword
      })
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Get current user
  getCurrentUser: () => {
    return supabase.auth.getUser()
  },

  // Listen to auth changes
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback)
  }
}

// Database helper functions
export const dbHelpers = {
  // Generic insert function
  insert: async (table, data) => {
    try {
      const { data: result, error } = await supabase
        .from(table)
        .insert(data)
        .select()
      return { data: result, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Generic select function
  select: async (table, columns = '*', filters = {}) => {
    try {
      let query = supabase.from(table).select(columns)
      
      // Apply filters if provided
      Object.entries(filters).forEach(([key, value]) => {
        query = query.eq(key, value)
      })
      
      const { data, error } = await query
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Generic update function
  update: async (table, updates, filters) => {
    try {
      let query = supabase.from(table).update(updates)
      
      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        query = query.eq(key, value)
      })
      
      const { data, error } = await query.select()
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  },

  // Generic delete function
  delete: async (table, filters) => {
    try {
      let query = supabase.from(table)
      
      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        query = query.delete().eq(key, value)
      })
      
      const { data, error } = await query
      return { data, error }
    } catch (error) {
      return { data: null, error }
    }
  }
}